import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  AreaChart as ReAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { mapAndSortLeadCount, PeriodView } from "./helpers/chartPeriod";

type LeadCountItem = {
  period: string;
  highIntent: number;
  mediumIntent: number;
  lowIntent: number;
};

const AreaChart = ({
  leadCount = [] as LeadCountItem[],
  view = "date",
}: {
  leadCount?: LeadCountItem[];
  view?: PeriodView;
}) => {
  const data = useMemo(() => {
    const base = (leadCount || []).map((item) => ({
      period: String(item.period ?? ""),
      hot: Number(item.highIntent ?? 0),
      warm: Number(item.mediumIntent ?? 0),
      interested: Number(item.lowIntent ?? 0),
    }));

    return mapAndSortLeadCount(base, view);
  }, [leadCount, view]);

  return (
    <div className="chart-container">
      <ResponsiveContainer width="100%" height={330}>
        <ReAreaChart
          data={data}
          margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            opacity={0.25}
          />

          <defs>
            <linearGradient id="fillHot" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#065f46" stopOpacity={0.35} />
              <stop offset="95%" stopColor="#065f46" stopOpacity={0.03} />
            </linearGradient>
            <linearGradient id="fillWarm" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3730a3" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#3730a3" stopOpacity={0.03} />
            </linearGradient>
            <linearGradient id="fillInterested" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#374151" stopOpacity={0.28} />
              <stop offset="95%" stopColor="#374151" stopOpacity={0.03} />
            </linearGradient>
          </defs>

          <XAxis
            dataKey="period"
            tickLine={false}
            axisLine={false}
            minTickGap={18}
            tick={{ fontSize: 12 }}
          />

          <YAxis
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            tick={{ fontSize: 12 }}
          />

          <Tooltip
            cursor={{ opacity: 0.08 }}
            contentStyle={{
              borderRadius: 12,
              border: "1px solid rgba(0,0,0,0.08)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            }}
            labelStyle={{ fontWeight: 600 }}
          />

          <Legend verticalAlign="top" height={30} iconType="circle" />

          <Area
            type="monotone"
            dataKey="hot"
            name="High Intent"
            stroke="#065f46"
            strokeWidth={2}
            fill="url(#fillHot)"
            dot={false}
            activeDot={{ r: 5 }}
          />
          <Area
            type="monotone"
            dataKey="warm"
            name="Medium Intent"
            stroke="#3730a3"
            strokeWidth={2}
            fill="url(#fillWarm)"
            dot={false}
            activeDot={{ r: 5 }}
          />
          <Area
            type="monotone"
            dataKey="interested"
            name="Low Intent"
            stroke="#374151"
            strokeWidth={2}
            fill="url(#fillInterested)"
            dot={false}
            activeDot={{ r: 5 }}
          />
        </ReAreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AreaChart;
